import { ChevronRight, TriangleAlert } from 'lucide-react';

import type { MatchInquiredMatch, MatchTerminatedMatch } from '../types';
import { ctw } from '@/common';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components';
import { MatchingProperties } from './MatchingProperties';

type MatchRow = MatchTerminatedMatch | MatchInquiredMatch;

const hasTerminationReason = (row: MatchRow): row is MatchTerminatedMatch =>
  'terminationReason' in row;

const formatDate = (value: string) => {
  const date = new Date(value);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');

  return `${mm}/${dd}/${date.getFullYear()}`;
};

const HeaderCell = ({ label, className }: { label: string; className?: string }) => (
  <span className={ctw('text-sm font-medium text-slate-500', className)}>{label}</span>
);

/**
 * Terminated- or Inquired-merchant matches table. Each row expands to reveal its
 * "Matching Properties" block. `variant` toggles the Termination Reason column.
 */
export const MatchesTable = ({
  title,
  matches,
  variant,
}: {
  title: string;
  matches: MatchRow[];
  variant: 'terminated' | 'inquired';
}) => {
  const showTerminationReason = variant === 'terminated';

  const gridCols = showTerminationReason
    ? 'grid-cols-[1.5fr_1fr_1fr_1.5fr_1fr]'
    : 'grid-cols-[2fr_1fr_1fr_1fr]';

  return (
    <section className="space-y-2">
      <h3 className="text-base font-bold">{title}</h3>

      <div className="rounded-lg border border-slate-200">
        <div className={ctw('grid items-center gap-2 px-4 py-3', gridCols)}>
          <HeaderCell label="Name" />
          <HeaderCell label="Exact Matches" />
          <HeaderCell label="Partial Matches" />
          {showTerminationReason && <HeaderCell label="Termination Reason" />}
          <HeaderCell label="Date Added" className="text-right" />
        </div>

        {matches.length === 0 && (
          <p className="text-muted-foreground px-4 py-6 text-sm">No matches found.</p>
        )}

        <Accordion type="multiple">
          {matches.map((match, index) => (
            <AccordionItem
              key={`${match.name}-${index}`}
              value={`${match.name}-${index}`}
              className="border-t border-slate-100"
            >
              <AccordionTrigger className="px-4 py-3 hover:no-underline [&>svg]:hidden">
                <div className={ctw('grid w-full items-center gap-2 text-left', gridCols)}>
                  <span className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform [[data-state=open]_&]:rotate-90" />
                    {showTerminationReason && (
                      <TriangleAlert className="h-4 w-4 shrink-0 text-amber-500" />
                    )}
                    <span className="notranslate truncate font-medium">{match.name}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-red-500" />
                    {match.exactMatches}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-orange-400" />
                    {match.partialMatches}
                  </span>
                  {showTerminationReason && hasTerminationReason(match) && (
                    <span className="text-sm">
                      ({match.terminationReason.code}) {match.terminationReason.label}
                    </span>
                  )}
                  <span className="text-right text-sm">{formatDate(match.dateAdded)}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-slate-50/60 px-4">
                <MatchingProperties matchingProperties={match.matchingProperties} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
